import React, { useEffect, useMemo, useState } from 'react';
import { AtelierNav, AtelierFooter, ATELIER_SCROLL_TARGET_KEY, scrollToId } from './AtelierChrome';
import './atelier.css';

/**
 * Fashdeck landing — "Atelier" (ported from Claude Design "Fashdeck Landing - Atelier").
 * Dark charcoal + saffron, Bricolage Grotesque / IBM Plex Mono.
 */

const ASSET = (name) => `/landing/atelier/${name}`;

const COPY = {
  Designers: {
    eyebrow: 'Fabric to fashion, powered by AI',
    headline: 'Design with fabric that actually exists.',
    sub: 'Discover sourceable fabrics, generate AI concepts on them, and send buyers a catalogue — without leaving Fashdeck.',
    ctaPrimary: 'Start designing free',
    ctaSecondary: "I'm a fabric supplier",
  },
  Suppliers: {
    eyebrow: 'For mills, weavers and fabric suppliers',
    headline: 'Put your fabric in front of the people designing with it.',
    sub: 'List your range once. Designers find it, render concepts on it in AI Studio, and order straight from your catalogue.',
    ctaPrimary: 'List your fabrics',
    ctaSecondary: "I'm a designer",
  },
};

const CHECK = 'linear-gradient(90deg,#ffffff1a 50%,transparent 50%),linear-gradient(#ffffff1a 50%,transparent 50%)';

const FABRICS = [
  { code: 'FD-1187', fibre: 'Cotton', name: 'Indigo gingham', spec: '120 GSM · Plain · MOQ 50 m', bg: '#3b4f7a', tex: CHECK, size: '24px 24px' },
  { code: 'FD-2041', fibre: 'Cotton', name: 'Rust twill', spec: '280 GSM · 2/1 twill · MOQ 100 m', bg: '#7a2f2a', tex: 'repeating-linear-gradient(45deg,#ffffff12 0 3px,transparent 3px 8px)' },
  { code: 'FD-0630', fibre: 'Linen', name: 'Natural linen', spec: '160 GSM · Plain · MOQ 30 m', bg: '#d9cdb4', tex: 'repeating-linear-gradient(0deg,#00000010 0 1px,transparent 1px 4px),repeating-linear-gradient(90deg,#00000010 0 1px,transparent 1px 4px)' },
  { code: 'FD-1530', fibre: 'Silk', name: 'Bottle green satin', spec: '90 GSM · Satin · MOQ 25 m', bg: '#2f5a48', tex: 'linear-gradient(120deg,#ffffff00 30%,#ffffff22 50%,#ffffff00 70%)' },
  { code: 'FD-0977', fibre: 'Wool', name: 'Ochre herringbone', spec: '340 GSM · Herringbone · MOQ 60 m', bg: '#b88a3a', tex: 'repeating-linear-gradient(45deg,#00000022 0 4px,transparent 4px 8px)' },
  { code: 'FD-1402', fibre: 'Linen', name: 'Plum slub', spec: '190 GSM · Slub · MOQ 40 m', bg: '#5a4a6a', tex: 'repeating-linear-gradient(90deg,#ffffff10 0 1px,transparent 1px 6px)' },
  { code: 'FD-0212', fibre: 'Silk', name: 'Sand raw silk', spec: '110 GSM · Dupion · MOQ 20 m', bg: '#9a8f80', tex: 'repeating-linear-gradient(0deg,#ffffff14 0 1px,transparent 1px 3px)' },
  { code: 'FD-0845', fibre: 'Cotton', name: 'Terracotta khadi', spec: '150 GSM · Handspun · MOQ 30 m', bg: '#c0623c', tex: 'repeating-linear-gradient(0deg,#00000012 0 1px,transparent 1px 3px),repeating-linear-gradient(90deg,#00000012 0 1px,transparent 1px 3px)' },
];
const FIBRE_FILTERS = ['All', 'Cotton', 'Linen', 'Silk', 'Wool'];

const COLLECTIONS = [
  ['velvet-coffee', 'Velvet Coffee Foil', ['velvet', 'soft', 'luxury']],
  ['slub-d6', 'Cotton Slub D6', []],
  ['velvet-rainbow', 'Velvet Rainbow Foil', ['velvet', 'dress', 'ethnic']],
  ['dog-fur', 'Faux fur zigzag', ['party', 'fringes', 'silky']],
  ['slub-d12', 'Cotton Slub D12', []],
  ['boucle', 'Bouclé print', []],
  ['jacquard-diamond', 'Satin Jacquard Diamond', []],
  ['slub-d10', 'Cotton Slub D10', []],
  ['suede', 'Suede leather', []],
  ['slub-d3', 'Cotton Slub D3', []],
  ['knit-texture', 'Knitting Textures', []],
  ['jacquard-checks', 'Satin Jacquard Checks', []],
].map(([key, name, tags]) => ({ key, img: ASSET(`col-${key}.jpg`), name, tags }));

const CATALOGUE_CHIPS = ['All', 'Cotton slub', 'Velvet', 'Jacquard', 'Knits'];

/* ---------------- Garment editor geometry ---------------- */

const NECK = {
  'Cowl Neck': () => 'C134,84 166,84 172,40',
  'Crew neck': () => 'Q150,64 172,40',
  'Deep V neck': (W) => `L150,${Math.min(140, W - 8)} L172,40`,
  'Draped Neck': () => 'Q138,92 150,118 Q162,92 172,40',
  'Halter Neck': () => 'Q150,112 172,40',
  'High Neck': () => 'L131,22 L169,22 L172,40',
};
const NECK_IMG = {
  'Cowl Neck': 'cowl', 'Crew neck': 'crew', 'Deep V neck': 'deepv',
  'Draped Neck': 'draped', 'Halter Neck': 'halter', 'High Neck': 'high',
};
const OPTS = {
  'Front neck': NECK,
  Sleeves: {
    Sleeveless: '',
    'Cap sleeve': 'M192,40 Q222,46 220,78 L202,88 Q197,62 192,40 Z',
    'Short sleeve': 'M192,40 L240,82 L220,106 L202,100 Z',
    'Puff sleeve': 'M192,40 Q252,34 238,98 Q220,108 202,100 Z',
    'Elbow sleeve': 'M192,40 L252,126 L232,138 L202,104 Z',
    'Full sleeve': 'M192,40 L262,206 L242,212 L202,104 Z',
  },
  Collar: {
    'No collar': '',
    'Mandarin collar': 'M150,28 L172,28 L174,44 L150,44 Z',
    'Peter Pan collar': 'M150,46 Q152,72 176,62 Q186,50 172,38 L150,40 Z',
    'Shirt collar': 'M150,50 L180,68 L178,36 L170,32 Z',
  },
  Length: { Mini: 255, Knee: 305, Midi: 350, Maxi: 405 },
  'Waist line': { Empire: 132, Natural: 170, 'Drop waist': 200 },
  'Flare variation': { Pencil: 0.06, 'A-line': 0.3, Flared: 0.55, Tiered: 0.5 },
  Colors: {
    Ikat: 'url(#fd-ikat)', Tweed: 'url(#fd-tweed)', Plum: '#5a4a6a',
    Rust: '#7a2f2a', Ivory: '#e9e2d4', 'Bottle green': '#2f5a48',
  },
};
const PART_TITLE = {
  'Front neck': 'Front neckline', Sleeves: 'Sleeves', Collar: 'Collars', Length: 'Length',
  'Waist line': 'Waist line', 'Flare variation': 'Flare variation', Colors: 'Colors',
};
const SWATCH_IMG = { Ikat: `url(${ASSET('fabric-ikat.png')})`, Tweed: `url(${ASSET('fabric-tweed.png')})` };
const SKIRT_PARTS = ['Length', 'Flare variation', 'Waist line'];
const UPPER_PARTS = ['Front neck', 'Sleeves', 'Collar'];

const INITIAL_SEL = {
  'Front neck': 'Deep V neck', Sleeves: 'Puff sleeve', Collar: 'No collar', Length: 'Midi',
  'Waist line': 'Natural', 'Flare variation': 'Tiered', Colors: 'Ikat',
};

const geom = (s) => {
  const W = OPTS['Waist line'][s['Waist line']];
  const Y = OPTS.Length[s.Length];
  const k = OPTS['Flare variation'][s['Flare variation']];
  const hw = (y) => 32 + (y - W) * k;
  const h = hw(Y);
  const tiers = s['Flare variation'] === 'Tiered'
    ? [1 / 3, 2 / 3].map((t) => {
      const y = W + (Y - W) * t;
      const q = hw(y);
      return `M${150 - q},${y} Q150,${y + 8} ${150 + q},${y}`;
    }).join(' ')
    : '';
  return {
    bodice: `M108,40 L128,40 ${NECK[s['Front neck']](W)} L192,40 Q196,75 202,104 L182,${W} L118,${W} L98,104 Q104,75 108,40 Z`,
    sleeve: OPTS.Sleeves[s.Sleeves],
    collar: OPTS.Collar[s.Collar],
    skirt: `M118,${W} L182,${W} L${150 + h},${Y} Q150,${Y + 14} ${150 - h},${Y} Z`,
    tiers,
  };
};

const MIRROR = 'translate(300,0) scale(-1,1)';

/** Renders a garment; symmetric parts (sleeve, collar) are drawn twice, mirrored. */
const Garment = ({ g, fill, stroke = '#2b2724', widths = {}, dashes = {}, tierWidth = 1.2 }) => {
  const p = (key) => ({
    d: g[key] || undefined,
    fill,
    stroke,
    strokeWidth: widths[key] ?? 2,
    strokeDasharray: dashes[key] ?? 'none',
    strokeLinejoin: 'round',
  });
  return (
    <>
      {g.sleeve && <g transform={MIRROR}><path {...p('sleeve')} /></g>}
      {g.sleeve && <path {...p('sleeve')} />}
      <path {...p('skirt')} />
      {g.tiers && <path d={g.tiers} fill="none" stroke={stroke} strokeWidth={tierWidth} />}
      <path {...p('bodice')} />
      {g.collar && <g transform={MIRROR}><path {...p('collar')} /></g>}
      {g.collar && <path {...p('collar')} />}
    </>
  );
};

const StudioEditor = () => {
  const [part, setPart] = useState('Front neck');
  const [sel, setSel] = useState(INITIAL_SEL);

  const on = (parts) => parts.includes(part);
  const w = (parts) => (on(parts) ? 2.6 : 1.4);
  const d = (parts) => (on(parts) ? '6 4' : 'none');
  const g = geom(sel);
  const fill = OPTS.Colors[sel.Colors];
  const widths = { bodice: w(['Front neck', 'Waist line']), sleeve: w(['Sleeves']), collar: w(['Collar']), skirt: w(SKIRT_PARTS) };
  const dashes = { bodice: d(['Front neck', 'Waist line']), sleeve: d(['Sleeves']), collar: d(['Collar']), skirt: d(SKIRT_PARTS) };

  const upper = UPPER_PARTS.includes(part);
  const summary = [
    sel['Front neck'], sel.Sleeves, sel.Collar === 'No collar' ? null : sel.Collar,
    sel.Length, sel['Waist line'], sel['Flare variation'], sel.Colors,
  ].filter(Boolean).join(' · ');

  return (
    <div className="atl-panel">
      <div className="atl-studio">
        <div className="atl-card-white">
          <div className="atl-canvas">
            <div className="atl-input-fabric">
              <span>Input fabric</span>
              <span style={{ background: SWATCH_IMG[sel.Colors] || fill, backgroundSize: 'cover', backgroundPosition: 'center' }} />
            </div>
            <svg viewBox="0 0 300 430" role="img" aria-label={`Dress preview: ${summary}`}>
              <defs>
                <pattern id="fd-ikat" patternUnits="userSpaceOnUse" width="90" height="110">
                  <image href={ASSET('fabric-ikat.png')} x="0" y="0" width="90" height="110" preserveAspectRatio="xMidYMid slice" />
                </pattern>
                <pattern id="fd-tweed" patternUnits="userSpaceOnUse" width="70" height="46">
                  <image href={ASSET('fabric-tweed.png')} x="0" y="0" width="70" height="46" preserveAspectRatio="xMidYMid slice" />
                </pattern>
              </defs>
              <Garment g={g} fill={fill} widths={widths} dashes={dashes} />
            </svg>
          </div>
          <div className="atl-summary">{summary}</div>
          <div className="atl-ai-edit">
            <div style={{ fontSize: 14, fontWeight: 600 }}>Custom AI edit</div>
            <div style={{ fontSize: 15, color: '#9a938c' }}>Describe any change (background, styling, colors, drape…)</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <span className="atl-ai-edit-btn">Apply AI edit</span>
            </div>
          </div>
        </div>

        <div className="atl-card-white">
          <div>
            <div style={{ fontSize: 12, letterSpacing: '.06em', color: '#6f6862' }}>FASHDECK STUDIO EDITOR</div>
            <div style={{ marginTop: 4, fontSize: 17, color: '#2b2724' }}>Customize your design</div>
          </div>
          <div className="atl-parts">
            {Object.keys(OPTS).map((name) => (
              <button
                key={name}
                type="button"
                className={`atl-part${name === part ? ' is-on' : ''}`}
                aria-pressed={name === part}
                onClick={() => setPart(name)}
              >
                {name}
              </button>
            ))}
          </div>
          <div className="atl-active-title">
            <div style={{ fontSize: 19, fontWeight: 600, color: '#141413' }}>{PART_TITLE[part]}</div>
            <div style={{ marginTop: 4, fontSize: 14, color: '#6f6862' }}>
              Choose one reference. Your edit applies to the main preview after a moment.
            </div>
          </div>
          <div className="atl-options">
            {Object.keys(OPTS[part]).map((label) => {
              const active = sel[part] === label;
              const isNeck = part === 'Front neck';
              const isColor = part === 'Colors';
              return (
                <button
                  key={label}
                  type="button"
                  className={`atl-option${active ? ' is-on' : ''}`}
                  aria-pressed={active}
                  onClick={() => setSel((s) => ({ ...s, [part]: label }))}
                >
                  <span className="atl-option-thumb">
                    {isNeck ? (
                      <img src={ASSET(`neck-${NECK_IMG[label]}.png`)} alt={label} loading="lazy" />
                    ) : (
                      <svg viewBox={upper ? '60 14 180 150' : '40 14 220 410'} aria-hidden="true">
                        <Garment
                          g={geom({ ...sel, [part]: label })}
                          fill={isColor ? OPTS.Colors[label] : '#dccaf3'}
                          stroke={isColor ? '#2b2724' : '#a98bd6'}
                          tierWidth={1.5}
                        />
                      </svg>
                    )}
                  </span>
                  <span style={{ textAlign: 'center' }}>{label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------------- Page ---------------- */

const AtelierLanding = ({
  heroLead = 'Designers',
  showSupplier = true,
  onStartDesigning,
  onSignIn,
  onSupplierSignup,
  onOpenCatalogue,
  onPricing,
  onAbout,
  onPrivacy,
  onHelp,
}) => {
  const [filter, setFilter] = useState('All');
  const copy = COPY[heroLead] || COPY.Designers;
  const fabrics = useMemo(
    () => (filter === 'All' ? FABRICS : FABRICS.filter((x) => x.fibre === filter)),
    [filter]
  );

  // Arriving from another Atelier page (e.g. About → "AI Studio"): jump to that section.
  useEffect(() => {
    let target;
    try {
      target = window.sessionStorage.getItem(ATELIER_SCROLL_TARGET_KEY);
    } catch (_) {}
    if (!target) return undefined;
    // Wait past the parent's scroll-to-top on mount. Clear the key only once we actually
    // scroll, so a StrictMode mount/unmount/mount cycle doesn't lose it.
    const t = setTimeout(() => {
      try { window.sessionStorage.removeItem(ATELIER_SCROLL_TARGET_KEY); } catch (_) {}
      // Instant jump: a smooth scroll this early gets cancelled by images still laying out.
      scrollToId(target, 'auto');
    }, 150);
    return () => clearTimeout(t);
  }, []);

  const navLinks = [
    ['Fabric library', () => scrollToId('library')],
    ['AI Studio', () => scrollToId('studio')],
    ['Catalogues', () => scrollToId('catalogues')],
    ...(showSupplier ? [['For suppliers', () => scrollToId('suppliers')]] : []),
    ['About', onAbout],
    ['Pricing', onPricing],
  ];

  const heroPrimary = heroLead === 'Suppliers' ? onSupplierSignup : onStartDesigning;
  const heroSecondary = heroLead === 'Suppliers' ? onStartDesigning : () => scrollToId('suppliers');

  return (
    <div className="atl-root">
      <AtelierNav
        links={navLinks}
        onLogo={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        onSignIn={onSignIn}
        onStartDesigning={onStartDesigning}
      />

      <header className="atl-hero atl-wrap">
        <div className="atl-pill"><span className="atl-dot" />{copy.eyebrow}</div>
        <h1>{copy.headline}</h1>
        <p>{copy.sub}</p>
        <div className="atl-hero-ctas">
          <button type="button" className="atl-link atl-btn atl-btn--lg" onClick={heroPrimary}>{copy.ctaPrimary}</button>
          <button type="button" className="atl-link atl-btn atl-btn--lg atl-btn--ghost" onClick={heroSecondary}>{copy.ctaSecondary}</button>
        </div>
      </header>

      <div className="atl-swatches atl-wrap">
        <div aria-hidden="true" className="atl-swatch atl-swatch--s" style={{ background: '#3b4f7a', backgroundImage: CHECK, backgroundSize: '28px 28px' }} />
        <div aria-hidden="true" className="atl-swatch atl-swatch--m" style={{ background: '#7a2f2a', backgroundImage: 'repeating-linear-gradient(45deg,#ffffff12 0 3px,transparent 3px 8px)' }} />
        <div className="atl-concept">
          <figure className="atl-concept-fabric">
            <img src="/fab.jpg" alt="Printed jacquard fabric with palms, elephants and peacocks" loading="eager" />
            <figcaption>Fabric</figcaption>
          </figure>
          <span className="atl-concept-arrow" aria-hidden="true">→</span>
          <figure className="atl-concept-render">
            <img src="/sunny.jpg" alt="AI concept render: a kurta made from the same printed fabric" loading="eager" />
            <figcaption><span className="atl-tag">AI render</span></figcaption>
          </figure>
        </div>
        <div aria-hidden="true" className="atl-swatch atl-swatch--m" style={{ background: '#d9cdb4', backgroundImage: 'repeating-linear-gradient(0deg,#00000010 0 1px,transparent 1px 4px),repeating-linear-gradient(90deg,#00000010 0 1px,transparent 1px 4px)' }} />
        <div aria-hidden="true" className="atl-swatch atl-swatch--s" style={{ background: '#2f5a48', backgroundImage: 'repeating-linear-gradient(-45deg,#00000026 0 6px,transparent 6px 12px)' }} />
      </div>

      <section className="atl-section atl-wrap">
        <div className="atl-section-head" style={{ marginBottom: 40 }}>
          <h2 className="atl-h2">One deck for the whole design-to-buyer loop.</h2>
          <p className="atl-lede" style={{ maxWidth: 340 }}>
            Each step hands off to the next, so fabric specs follow the design all the way to the order.
          </p>
        </div>
        <div className="atl-loop">
          <button type="button" className="atl-link atl-loop-card" onClick={() => scrollToId('library')}>
            <div className="atl-loop-visual atl-loop-visual--grid">
              {['#3b4f7a', '#7a2f2a', '#d9cdb4', '#2f5a48', '#e8b04a', '#5a4a6a', '#9a8f80', '#c0623c'].map((c) => (
                <div key={c} style={{ background: c }} />
              ))}
            </div>
            <div className="atl-eyebrow" style={{ marginBottom: 0 }}>01 · Source</div>
            <h3>Fabric library</h3>
            <p>Filter by fibre, GSM, weave and MOQ. Order swatches or bulk direct from suppliers.</p>
          </button>
          <button type="button" className="atl-link atl-loop-card" onClick={() => scrollToId('studio')}>
            <div className="atl-loop-visual atl-loop-visual--media">
              <img src="/studio.gif" alt="AI Studio turning a prompt into a garment concept" loading="lazy" />
            </div>
            <div className="atl-eyebrow" style={{ marginBottom: 0 }}>02 · Design</div>
            <h3>AI Studio</h3>
            <p>Describe a garment, pick a fabric, get concepts rendered in that exact material.</p>
          </button>
          <button type="button" className="atl-link atl-loop-card" onClick={() => scrollToId('catalogues')}>
            <div className="atl-loop-visual atl-loop-visual--media">
              <img src="/catalog.gif" alt="A shared Fashdeck catalogue being browsed" loading="lazy" />
            </div>
            <div className="atl-eyebrow" style={{ marginBottom: 0 }}>03 · Sell</div>
            <h3>Catalogues</h3>
            <p>Share a branded catalogue by link. Buyers shortlist, comment and request quotes.</p>
          </button>
        </div>
      </section>

      <section id="library" className="atl-section atl-wrap">
        <div className="atl-eyebrow">Fabric library</div>
        <div className="atl-section-head">
          <h2 className="atl-h2">Every swatch is a spec sheet.</h2>
          <div className="atl-filters" role="group" aria-label="Filter by fibre">
            {FIBRE_FILTERS.map((label) => (
              <button
                key={label}
                type="button"
                className={`atl-filter${label === filter ? ' is-on' : ''}`}
                aria-pressed={label === filter}
                onClick={() => setFilter(label)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="atl-fabrics">
          {fabrics.map((x) => (
            <div key={x.code} className="atl-fabric">
              <div className="atl-fabric-swatch" style={{ background: x.bg, backgroundImage: x.tex, backgroundSize: x.size || 'auto' }} />
              <div className="atl-fabric-body">
                <div className="atl-fabric-meta"><span>{x.code}</span><span>{x.fibre}</span></div>
                <div className="atl-fabric-name">{x.name}</div>
                <div className="atl-fabric-spec">{x.spec}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="studio" className="atl-section atl-wrap">
        <div className="atl-section-head">
          <div>
            <div className="atl-eyebrow">AI Studio · Design editor</div>
            <h2 className="atl-h2" style={{ maxWidth: 680 }}>Edit the garment, one part at a time.</h2>
          </div>
          <p className="atl-lede">Pick a part, choose a reference, and only that part changes. Try it.</p>
        </div>
        <StudioEditor />
      </section>

      <section id="catalogues" className="atl-section atl-wrap">
        <div className="atl-section-head">
          <div>
            <div className="atl-eyebrow">Catalogues</div>
            <h2 className="atl-h2">A link your buyers actually open.</h2>
          </div>
          <div style={{ maxWidth: 380 }}>
            <p className="atl-lede">
              Group fabrics and designs into collections, share one link, and let buyers browse, filter and shortlist.
            </p>
            <button type="button" className="atl-link atl-text-link" onClick={onOpenCatalogue}>
              See an example catalogue →
            </button>
          </div>
        </div>
        <div className="atl-panel">
          <div className="atl-cat-bar">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
              <span className="atl-avatar">S</span>
              <div>
                <div style={{ fontSize: 17, fontWeight: 600 }}>Shubham Textiles</div>
                <div style={{ fontSize: 13, color: '#6f6862' }}>Showing 31 collections</div>
              </div>
            </div>
            <div className="atl-cat-chips">
              {CATALOGUE_CHIPS.map((c, i) => (
                <span key={c} className={`atl-chip${i === 0 ? ' is-on' : ''}`}>{c}</span>
              ))}
            </div>
          </div>
          <div className="atl-collections">
            {COLLECTIONS.map((c) => (
              <button key={c.key} type="button" className="atl-link atl-collection" onClick={onOpenCatalogue}>
                <img src={c.img} alt={c.name} loading="lazy" />
                <div className="atl-collection-body">
                  <div style={{ fontSize: 18, fontWeight: 500, color: '#141413' }}>{c.name}</div>
                  <div style={{ fontSize: 14, color: '#6f6862' }}>Shubham</div>
                  {c.tags.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                      {c.tags.map((t) => <span key={t} className="atl-collection-tag">{t}</span>)}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {showSupplier && (
        <section id="suppliers" className="atl-supplier-wrap">
          <div className="atl-supplier">
            <h2>Supply fabric? Get it designed with.</h2>
            <div style={{ maxWidth: 360 }}>
              <p style={{ margin: '0 0 24px', fontSize: 18, lineHeight: 1.5 }}>
                List your range once. Designers use it in AI Studio and order straight from your catalogue.
              </p>
              <button type="button" className="atl-link atl-btn atl-btn--dark" onClick={onSupplierSignup}>
                List your fabrics
              </button>
            </div>
          </div>
        </section>
      )}

      <section className="atl-final atl-wrap">
        <h2>Your next collection starts with a swatch.</h2>
        <button
          type="button"
          className="atl-link atl-btn atl-btn--xl"
          style={{ marginTop: 36 }}
          onClick={onStartDesigning}
        >
          Start designing free
        </button>
      </section>

      <AtelierFooter onAbout={onAbout} onPricing={onPricing} onHelp={onHelp} onPrivacy={onPrivacy} />
    </div>
  );
};

export default AtelierLanding;
